<template>
  <v-form ref="form">
    <v-row
      dense
      class="mb-4"
    >
      <v-col cols="6">
        <v-text-field
          v-model="action.name"
          :rules="rules.name"
          clearable
          dense
          outlined
          :label="$t('commons.name')"
        />
      </v-col>
      <v-col cols="6">
        <v-select
          v-model="action.type_action"
          :items="actionTypes"
          :rules="rules.type_action"
          dense
          outlined
          :label="$t('commons.type')"
        />
      </v-col>
      <v-col cols="12">
        <label>{{ $t("commons.action_description") }}</label>
        <ckeditor
          :editor="editor"
          tag-name="textarea"
          rows="3"
          v-model="action.description"
          :config="editorConfig"
          :label="$t('commons.action_description')"
          class="content-element"
        ></ckeditor>
      </v-col>
      <v-col cols="4">
        <v-select
          v-model="action.user"
          :items="users"
          :rules="rules.user"
          dense
          outlined
          item-text="email"
          item-value="id"
          :label="$t('commons.intended_for')"
        />
      </v-col>
      <v-col cols="4">
        <DatetimePicker
          v-model="action.end_date"
          :min="nonConformity.created_at"
          :textFieldProps="{
            dense: true,
            label: $t('commons.date.end_date'),
            rules: rules.endDate
          }"
        ></DatetimePicker>
      </v-col>
      <v-col cols="4">
        <v-select
          v-model="action.status"
          :items="statusItems"
          :rules="rules.status"
          dense
          outlined
          :label="$t('commons.status.name')"
        ></v-select>
      </v-col>
    </v-row>
    <template v-if="action.type_action !== 'Curative'">
      <v-divider>
        <span class="text-center">{{ $t("commons.monitoring") }}</span>
      </v-divider>
      <v-row
        dense
        class="mt-4"
      >
        <v-col cols="12">
          <label>{{ $t("commons.monitoring_description") }}</label>
          <ckeditor
            :editor="editor"
            tag-name="textarea"
            rows="3"
            v-model="action.monitoring_description"
            :config="editorConfig"
            :label="$t('commons.monitoring_description')"
            class="content-element"
          ></ckeditor>
        </v-col>
        <v-col cols="4">
          <v-select
            v-model="action.monitoring_user"
            :items="users"
            :rules="rules.monitoring_user"
            dense
            outlined
            item-text="email"
            item-value="id"
            :label="$t('commons.monitoring_intended_for')"
          />
        </v-col>
        <v-col cols="4">
          <DatetimePicker
            v-model="action.monitoring_end_date"
            :min="nonConformity.created_at"
            :textFieldProps="{
              dense: true,
              label: $t('commons.monitoring_end_date'),
              rules: rules.endDate
            }"
          ></DatetimePicker>
        </v-col>
        <v-col cols="4">
          <v-select
            v-model="action.monitoring_status"
            :items="statusItems"
            dense
            outlined
            :label="$t('commons.status.name')"
          ></v-select>
        </v-col>
      </v-row>
    </template>
    <v-row dense>
      <v-col cols="12">
        <v-btn
          color="primary"
          class="mb-2"
          @click="addCostRow"
        >
          {{ $t("commons.add_cost") }}
        </v-btn>
        <v-simple-table v-if="costs.length > 0">
          <thead>
            <tr>
              <th>{{ $t("commons.hour_rate") }} [€]</th>
              <th>{{ $t("commons.nb_hour") }}</th>
              <th>{{ $t("commons.material_equipment") }} [€]</th>
              <th>{{ $t("commons.total") }} [€]</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(cost, index) in costs"
              :key="index"
            >
              <td>
                <v-text-field
                  v-model="cost.taux_horaire"
                  :rules="rules.decimal"
                  dense
                />
              </td>
              <td>
                <v-text-field
                  v-model="cost.nb_heures"
                  :rules="rules.decimal"
                  dense
                />
              </td>
              <td>
                <v-text-field
                  v-model="cost.matiere_equipement"
                  :rules="rules.decimal"
                  dense
                />
              </td>
              <td>{{ calculateTotal(cost) }} €</td>
              <td>
                <v-btn
                  icon
                  @click="removeCostRow(index)"
                  color="red"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td
                colspan="3"
                style="font-size: 1.2em; padding: 16px"
              >
                {{ $t("commons.total_general") }}
              </td>
              <td style="font-size: 1.2em; padding: 16px">
                <b>{{ calculateGrandTotal }} €</b>
              </td>
            </tr>
          </tfoot>
        </v-simple-table>
      </v-col>
    </v-row>

    <v-row class="pb-4 pr-4 d-flex align-center justify-end">
      <avs-button
        block
        color="primary white--text"
        class="mr-4"
        noSpantext
        :disabled="isDisabled()"
        @click="sign(action.id)"
      >
        <template v-slot:name>
          <span class="mdi mdi-draw"></span>
          {{ $t("commons.sign") }}
        </template>
      </avs-button>
      <template v-if="saving">
        <v-progress-circular
          indeterminate
          size="20"
          color="primary"
        ></v-progress-circular>
      </template>
      <avs-saveButton
        v-else
        :disabled="isSaved || isSign || isClosed"
        @click="handleSave"
      />
    </v-row>
  </v-form>
</template>

<script>
import ClassicEditor from "@ckeditor/ckeditor5-build-classic"
import { required, decimal } from "@/utils/validator"
import DatetimePicker from "@/components/inputs/DatetimePicker.vue"
import TeamService from "@/services/TeamService"
import NonConformityService from "@/services/NonConformityService"
import { STATUS_ITEMS, STATUS_ITEMS_DISABLED } from "@/utils/consts"
import { isInspector } from "@/utils/middleware"
export default {
  components: {
    DatetimePicker
  },
  name: "NonConformityAction",
  props: {
    value: {
      type: Object,
      required: true
    },
    isSaved: {
      type: Boolean,
      default: false
    },
    nonConformity: {
      type: Object,
      required: true
    },
    projectId: {
      type: String,
      required: true
    }
  },
  created() {
    this.fetchUser()
    this.checkIfActionIsSigned()
  },
  data() {
    return {
      costs: [],
      action: { ...this.value, description: this.value.description || "" },
      actionTypes: [
        { text: this.$t("commons.preventive"), value: "Preventive" },
        { text: this.$t("commons.corrective"), value: "Corrective" },
        { text: this.$t("commons.curative"), value: "Curative" }
      ],
      rules: {
        endDate: [required],
        name: [required],
        non_conformity: [required],
        type_action: [required],
        status: [required],
        user: [required],
        decimal: [required, decimal]
      },
      users: [],
      saving: false,
      editor: ClassicEditor,
      editorConfig: {
        toolbar: ["heading", "|", "bold", "italic", "link", "|", "undo", "redo"]
      },
      isClosed: false,
      isSign: false
    }
  },
  computed: {
    statusItems() {
      return isInspector(this.projectRole) ? STATUS_ITEMS : STATUS_ITEMS_DISABLED
    },
    projectRole() {
      return this.$store.state.app.project.role
    },
    calculateGrandTotal() {
      // Calcul du total général réactif
      return this.costs.reduce((sum, cost) => sum + this.calculateTotal(cost), 0)
    }
  },
  methods: {
    async fetchUser() {
      const response = await TeamService.getTeam(this, this.projectId)
      this.users = response
    },
    async updateAction() {
      this.saving = true
      this.action.lang = this.$i18n.locale
      return NonConformityService.updateAction(this.action.id, this.action)
        .then(response => {
          this.$emit("update", response)
          this.send(
            this.$t("messages.success.updated", { entity: this.$t("commons.action") }),
            "success"
          )
          this.saving = false
          if (this.action.status == "Closed") {
            this.isClosed = true
          }
        })
        .catch(err => {
          if (err.response && err.response.status === 403) {
            // alert("Access denied: You do not have permission to access this resource.")
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.permission_denied"),
              color: "error"
            })
          }
        })
    },
    async createAction() {
      this.saving = true
      this.action.lang = this.$i18n.locale
      return NonConformityService.createAction(this.nonConformity.id, this.action)
        .then(response => {
          this.$emit("update", response)
          this.send(
            this.$t("messages.success.saved", { entity: this.$t("commons.action") }),
            "success"
          )
          this.saving = false
          if (this.action.status == "Closed") {
            this.isClosed = true
          }
        })
        .catch(err => {
          if (err.response && err.response.status === 403) {
            // alert("Access denied: You do not have permission to access this resource.")
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.permission_denied"),
              color: "error"
            })
          }
        })
    },
    addCostRow() {
      // Ajouter une nouvelle ligne vide pour les coûts
      this.costs.push({
        taux_horaire: 0,
        nb_heures: 0,
        matiere_equipement: 0
      })
    },
    calculateTotal(cost) {
      // Calcul du total pour une ligne
      return cost.taux_horaire * cost.nb_heures + parseFloat(cost.matiere_equipement)
    },
    removeCostRow(index) {
      this.costs.splice(index, 1) // Supprime la ligne à l'index spécifié
    },
    handleSave() {
      if (this.validate()) {
        this.action.costs = this.costs
        const p = this.action.id ? this.updateAction() : this.createAction()
        p.then(e => {
          this.$emit("isSaved", true)
        })
      }
    },
    isDisabled() {
      return this.isClosed || this.isSign || !this.action.id
    },
    send(msg, color) {
      this.$store.commit("app/message", { msg, color })
    },
    validate() {
      return this.$refs.form.validate()
    },
    sign(actionId) {
      // Si aucune image, appelez simplement la fonction post avec l'ID du document
      NonConformityService.getActionSignatures(actionId)
        .then(r => {
          NonConformityService.postActionSignature(actionId).then(v => {
            this.$emit("isSigned", true)
            this.isSigned = true
            this.isClosed = true
          })
        })
        .catch(e => {
          if (e.response && e.response.status === 403) {
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.permission_denied"),
              color: "error"
            })
          } else {
            this.$emit("error", e)
          }
        })
    },
    async checkIfActionIsSigned() {
      if (this.action.id) {
        try {
          const signatures = await NonConformityService.getActionSignatures(this.action.id)
          if (signatures && signatures.length > 0) {
            this.isSign = true
            this.isClosed = true
          }
        } catch (err) {
          if (err.response && err.response.status === 403) {
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.permission_denied"),
              color: "error"
            })
          } else {
            this.$emit("error", err)
          }
        }
      }
    }
  },
  // watch: {
  //   action: {
  //     deep: true,
  //     handler(newValue, oldValue) {
  //       this.$emit("isSaved", false)
  //     }
  //   }
  // }
  watch: {
    // value: {
    //   immediate: true,
    //   deep: true,
    //   handler(newVal) {
    //     this.action = { ...newVal }
    //     // Charger les coûts de l'action lorsqu'ils sont disponibles
    //     this.costs = newVal.costs || []
    //     this.$emit("isSaved", false)
    //   }
    // }
    value: {
      immediate: true,
      deep: true,
      handler(newVal) {
        this.costs = newVal.costs ? [...newVal.costs] : [] // Charger les coûts s'ils existent
      }
    },
    action: {
      deep: true,
      handler(newValue, oldValue) {
        this.$emit("isSaved", false)
      }
    },
    costs: {
      deep: true,
      handler() {
        this.$emit("isSaved", false)
      }
    }
  }
}
</script>
